import { useIntl } from "react-intl";
import { snackbarOptions } from "../components/snackbar";
import { useSnackbar } from "notistack";

type ErrorData<T> = {
  status?: number;
  error?: string;
  type?: string;
  matcher?: (params: { error: any; value: T }) => boolean;
} & {
  [key: string]: any;
};

type ErrorRule<T> = [e: ErrorData<T>, getMessage: (params?: { [key: string]: any }) => string | null, action?: () => void];

/**
 * Szerver kérések hibakezelésére. Érvénytelen tokennél kiléptet, különben snackbar üzenetet ír a hibáról.
 */
export function useErrorHandler<T = any>(knownErrors?: ErrorRule<T> | ErrorRule<T>[]) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const rules = knownErrors !== undefined ? (Array.isArray(knownErrors) ? (knownErrors as ErrorRule<T>[]) : [knownErrors]) : [];

  const calcMessage = (e: any): { message: string | null; action?: () => void } => {
    if (typeof e === "object" && e.status !== undefined && e.data?.error !== undefined && typeof e.data.error === "string") {
      const {
        status,
        data: { error, type, ...params },
      } = e;
      const rule = rules.find(
        ([filter]) =>
          (filter.status === undefined || filter.status === status) &&
          (filter.error === undefined || filter.error === error) &&
          (filter.type === undefined || filter.type === type)
      );
      if (rule) {
        return { message: rule[1](params), action: rule[2] };
      }
      if (status === 403 || error === "A token is required for authentication.") {
        return { message: formatMessage({ defaultMessage: "Query error: token is required." }) };
      }
    }
    if (e instanceof Error) {
      return { message: e.message };
    }
    return { message: null };
  };

  const errorHandler = (error: any) => {
    const { message, action } = calcMessage(error);
    if (message) {
      enqueueSnackbar(
        message ?? formatMessage({ defaultMessage: "Something went wrong." }),
        snackbarOptions(
          {
            variant: "error",
            autoHideDuration: null,
          },
          { closeSnackbar }
        )
      );
    }
    if (action) {
      action();
    }
  };

  return { errorHandler };
}
